import React, { useEffect, useMemo, useState } from "react";
import { Search, ChevronDown, RefreshCw, Pencil, X, Lock, Unlock } from "lucide-react";
import { apiFetch } from "../utils/api";

interface WagerRow {
  id: number;
  name: string;
  email: string;
  wager: number;
  total_deposit: number;
  target: number;
  target_percentage: number;
  wager_multiplier?: number;
  is_locked?: boolean; // Lock status for target multiplier
}

interface ApiResponse {
  success: boolean;
  data?: any;
  message?: string;
}

const fmtAmt = (n: number | null | undefined) => {
  if (n == null || Number.isNaN(n)) return "-";
  return "₹" + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n);
};

const clampPct = (n: number) => Math.max(0, Math.min(100, n));

export default function UserWagerReports() {
  const [rows, setRows] = useState<WagerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState<keyof WagerRow>("target_percentage");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [globalTargetX, setGlobalTargetX] = useState<number>(3);
  const [editingUser, setEditingUser] = useState<WagerRow | null>(null);
  const [userTargetX, setUserTargetX] = useState<number>(3);
  const pageSize = 10;

  const columns = [
    { key: "id", label: "User ID" },
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "wager", label: "Wager (INR)" },
    { key: "total_deposit", label: "Total Deposit (INR)" },
    { key: "target", label: "Target (INR)" },
    { key: "target_percentage", label: "% Achieved" },
  ] as const;

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await apiFetch<ApiResponse>("/api/deposits/admin/all-wagers");

      if (!res?.success) throw new Error("Failed to fetch data");

      setRows(res.data || []);

    } catch (e: any) {
      setError(e?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const base = term
      ? rows.filter(r =>
        r.name.toLowerCase().includes(term) ||
        r.email.toLowerCase().includes(term) ||
        String(r.id).includes(term)
      )
      : rows;

    const sorted = [...base].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return sortDir === "asc" ? -1 : 1;
      if (vb == null) return sortDir === "asc" ? 1 : -1;

      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }

      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      return sortDir === "asc" ? (sa < sb ? -1 : 1) : (sa > sb ? -1 : 1);
    });

    return sorted;
  }, [rows, q, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const onSort = (key: keyof WagerRow) => {
    if (key === sortKey) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  // Update all users' target multiplier (only unlocked ones)
  const updateAllTargetX = async () => {
    try {
      if (!globalTargetX || globalTargetX <= 0) {
        return;
      }

      const res = await apiFetch(`/api/deposits/admin/update-all-targetx`, {
        method: "POST",
        body: JSON.stringify({ multiplier: globalTargetX })
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to update multiplier");
      }

      fetchData();
    } catch (err: any) {
      console.log(err)
    }
  };

  // Update individual user's target multiplier
  const updateUserTargetX = async () => {
    try {
      if (!userTargetX || !editingUser) {
        alert("Please enter a valid multiplier.");
        return;
      }

      const res = await apiFetch(`/api/deposits/admin/targetx/${editingUser.id}`, {
        method: "POST",
        body: JSON.stringify({ multiplier: userTargetX })
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to update user multiplier");
      }

      setEditingUser(null);
      fetchData();
    } catch (err: any) {
      alert(err.message || "Something went wrong");
    }
  };

  // Toggle lock status for a user
  const toggleLock = async (userId: number, currentLockStatus: boolean) => {
    console.log(currentLockStatus, 'current lock status')
    try {
      const res = await apiFetch(`/api/deposits/admin/targetx/${userId}/lock`, {
        method: "POST",
        body: JSON.stringify({ is_locked: !currentLockStatus })
      });

      console.log(res)

      if (!res?.success) {
        throw new Error(res?.message || "Failed to toggle lock");
      }

      // Update local state immediately for better UX
      setRows(prevRows =>
        prevRows.map(row =>
          row.id === userId
            ? { ...row, is_locked: !currentLockStatus }
            : row
        )
      );
    } catch (err: any) {
      console.error("Lock toggle error:", err);
      // Refresh data on error to ensure consistency
      fetchData();
    }
  };

  return (
    <div className="ml-0 lg:ml-[256px] p-2">
      <div className="max-w-7xl mx-auto p-6 space-y-6 bg-gray-50 min-h-screen">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">User Turnover Report</h1>
          <button
            onClick={fetchData}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 text-white px-4 py-2 hover:bg-blue-700 transition"
          >
            <RefreshCw className="w-5 h-5" /> Refresh
          </button>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:w-80">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => { setPage(1); setQ(e.target.value); }}
              placeholder="Search by name, email, or ID"
              className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="number"
              min="1"
              value={globalTargetX}
              onChange={(e) => setGlobalTargetX(Number(e.target.value))}
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="3"
            />
            <button
              onClick={updateAllTargetX}
              className="rounded-lg bg-green-600 text-white px-4 py-2 hover:bg-green-700 transition whitespace-nowrap"
            >
              Update All TargetX
            </button>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Sort by</label>
            <select
              value={String(sortKey)}
              onChange={(e) => setSortKey(e.target.value as keyof WagerRow)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              {columns.map(c => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
            <button
              onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              title="Toggle sort direction"
            >
              {sortDir === "asc" ? "Asc" : "Desc"}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-gray-600 text-lg animate-pulse">Loading wagers…</div>
        ) : error ? (
          <div className="text-red-600">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="text-gray-600">No results.</div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg shadow">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  {columns.map(c => (
                    <th
                      key={c.key}
                      onClick={() => onSort(c.key as keyof WagerRow)}
                      className="px-4 py-3 font-semibold text-left cursor-pointer select-none hover:bg-gray-200 transition"
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {sortKey === c.key && (
                          <ChevronDown className={`w-4 h-4 transition-transform ${sortDir === "desc" ? "rotate-180" : ""}`} />
                        )}
                      </span>
                    </th>
                  ))}
                  <th className="px-4 py-3 font-semibold text-left">Progress</th>
                  <th className="px-4 py-3 font-semibold text-left">Lock</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => {
                  const pct = clampPct(r.target_percentage);
                  const over = r.target_percentage > 100 ? `+${(r.target_percentage - 100).toFixed(1)}%` : "";

                  return (
                    <tr key={r.id} className="border-t hover:bg-gray-50 transition">
                      <td className="px-4 py-3 whitespace-nowrap text-gray-900">{r.id}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{r.name}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-700">{r.email}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmtAmt(r.wager)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmtAmt(r.total_deposit)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span>{fmtAmt(r.target)}</span>
                          <button
                            onClick={() => {
                              setEditingUser(r);
                              setUserTargetX(r.wager_multiplier || 3);
                            }}
                            className="text-blue-600 hover:text-blue-800 transition inline-flex items-center gap-1"
                            title="Edit target multiplier"
                          >
                            <Pencil className="w-4 h-4" />
                            {r.wager_multiplier && (
                              <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">
                                {r.wager_multiplier}x
                              </span>
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={r.target_percentage >= 100 ? "text-green-600 font-medium" : ""}>
                          {r.target_percentage.toFixed(2)}%
                          {over && <span className="text-green-500 ml-1"> {over}</span>}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1 min-w-[200px]">
                          <div className="w-full h-3 rounded-full bg-gray-200 overflow-hidden relative">
                            <div
                              className={`h-full transition-all duration-300 ${r.target_percentage >= 100 ? "bg-green-500" : "bg-blue-600"}`}
                              style={{ width: `${pct}%` }}
                            />
                            {r.target_percentage > 100 && (
                              <div className="absolute inset-0 flex items-center justify-end pr-1">
                                <span className="text-xs text-white font-bold">100%+</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center justify-between text-xs text-gray-600">
                            <span>{fmtAmt(r.wager)}</span>
                            <span className="font-medium">{r.target_percentage.toFixed(1)}%</span>
                            <span>{fmtAmt(r.target)}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          onClick={() => toggleLock(r.id, r.is_locked || false)}
                          className={`p-2 rounded-lg transition ${r.is_locked
                            ? "bg-red-100 text-red-600 hover:bg-red-200"
                            : "bg-green-100 text-green-600 hover:bg-green-200"
                            }`}
                          title={r.is_locked ? "Locked - Click to unlock" : "Unlocked - Click to lock"}
                        >
                          {r.is_locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {filtered.length > 0 && (
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              Showing {Math.min((page - 1) * pageSize + 1, filtered.length)}–
              {Math.min(page * pageSize, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                className="px-3 py-1 rounded-lg border border-gray-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              >Previous</button>
              <span>Page {page} / {totalPages}</span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                className="px-3 py-1 rounded-lg border border-gray-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Edit User Target Popup */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                Edit Target Multiplier
              </h3>
              <button
                onClick={() => setEditingUser(null)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">User ID: <span className="font-medium text-gray-900">{editingUser.id}</span></p>
                <p className="text-sm text-gray-600">Name: <span className="font-medium text-gray-900">{editingUser.name}</span></p>
                <p className="text-sm text-gray-600">Email: <span className="font-medium text-gray-900">{editingUser.email}</span></p>
                {editingUser.wager_multiplier && (
                  <p className="text-sm text-gray-600">Current Multiplier: <span className="font-medium text-blue-600">{editingUser.wager_multiplier}x</span></p>
                )}
                <p className="text-sm text-gray-600">
                  Status: <span className={`font-medium ${editingUser.is_locked ? "text-red-600" : "text-green-600"}`}>
                    {editingUser.is_locked ? "Locked" : "Unlocked"}
                  </span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Multiplier
                </label>
                <input
                  type="number"
                  min="1"
                  value={userTargetX}
                  onChange={(e) => setUserTargetX(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="3"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setEditingUser(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={updateUserTargetX}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  Update TargetX
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
